import { onSnapshot, type DocumentReference, type Query } from 'firebase/firestore';
import { useEffect, useState, type DependencyList } from 'react';

export interface DocState<T> {
  data?: T;
  loading: boolean;
  error?: Error;
}
export interface CollectionState<T> {
  data: T[];
  loading: boolean;
  error?: Error;
}

/**
 * Live document subscription. Loading is derived: until a snapshot for the
 * current ref path arrives, the hook reports `loading: true`.
 */
export function useDoc<T>(ref: DocumentReference<T> | null): DocState<T> {
  const path = ref?.path ?? null;
  const [state, setState] = useState<{ path: string | null } & DocState<T>>({ path: null, loading: false });
  useEffect(() => {
    if (!ref) return;
    return onSnapshot(
      ref,
      (snap) => setState({ path, data: snap.exists() ? snap.data() : undefined, loading: false }),
      (error) => setState({ path, loading: false, error }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
  if (!path) return { loading: false };
  if (state.path !== path) return { loading: true };
  return { data: state.data, loading: false, error: state.error };
}

/**
 * Live query subscription. `deps` identify the query (queries are rebuilt each
 * render, so callers pass the inputs they were built from).
 */
export function useCollection<T>(query: Query<T> | null, deps: DependencyList): CollectionState<T> {
  const key = query ? JSON.stringify(deps) : null;
  const [state, setState] = useState<{ key: string | null } & CollectionState<T>>({ key: null, data: [], loading: false });
  useEffect(() => {
    if (!query) return;
    return onSnapshot(
      query,
      (snap) => setState({ key, data: snap.docs.map((d) => d.data()), loading: false }),
      (error) => setState({ key, data: [], loading: false, error }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!key) return { data: [], loading: false };
  if (state.key !== key) return { data: [], loading: true };
  return { data: state.data, loading: false, error: state.error };
}
